import { TokenService } from "../../../../lib/tokenService";
import { getAppRegion, getTenantId } from "../../../../utils/getTenantId";
import { QuotationOperationContextStore } from "./quotationOperationContext";

export interface QuotationApiHeadersOptions {
	allowMissingAuth?: boolean;
	/** Sobrescreve o token JWT (cliente roteado / token de operacao). */
	accessTokenOverride?: string;
	/** Sobrescreve o tenant (cliente roteado / tenant efetivo). */
	tenantIdOverride?: string;
	/**
	 * Quando true (default), usa automaticamente o contexto de operacao roteado
	 * se ele estiver ativo. Passe false para forcar o contexto global.
	 */
	useOperationContext?: boolean;
}

/**
 * Monta headers padrao para APIs externas da cotacao (oneprint).
 * Inclui tenant e x-app-region quando disponiveis.
 *
 * Precedencia de contexto:
 * 1. overrides explicitos (accessTokenOverride / tenantIdOverride);
 * 2. contexto de operacao roteado ativo (se useOperationContext !== false);
 * 3. token/tenant globais (comportamento atual, sem regressao).
 */
export function buildQuotationApiHeaders(
	options: QuotationApiHeadersOptions = {}
): Record<string, string> {
	const operationOverride =
		options.useOperationContext === false
			? null
			: QuotationOperationContextStore.getOverride();

	const token =
		options.accessTokenOverride ??
		operationOverride?.accessToken ??
		TokenService.getAccessToken();

	const tenantId =
		options.tenantIdOverride ?? operationOverride?.tenantId ?? getTenantId();

	// x-app-region so eh exigido no fluxo global; no contexto roteado o tenant
	// destino ja resolve o banco no backend via X-Tenant-Id do token.
	const isRoutedContext =
		Boolean(options.accessTokenOverride) || Boolean(operationOverride);
	const appRegion = getAppRegion();

	if (!token && !options.allowMissingAuth) {
		throw new Error("Usuario nao autenticado. Faca login novamente.");
	}

	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		Accept: "application/json",
		...(token ? { Authorization: `Bearer ${token}` } : {}),
		...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
		...(appRegion && !isRoutedContext ? { "x-app-region": appRegion } : {}),
	};

	if (tenantId && !appRegion && !isRoutedContext) {
		throw new Error(`Tenant sem mapeamento de regiao: "${tenantId}".`);
	}

	return headers;
}

